import { useState } from "react";
import Tag from "components/common/Tag";
import { GenericDialog } from "components/common/dialogs/GenericDialog";
import { usePaymentContext } from "lib/contexts/PaymentProvider";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useLocale } from "lib/hooks/useLocale";
import { PaymentMode, UnApplicablePaymentReasonType } from "lib/types/payments";
import { SealPercent, X } from "@phosphor-icons/react";
import { Icon } from "@shopflo/ui/icon";
import CircularSpinner from "components/common/loaders/CircularSpinner";
import { currencyFormatter } from "lib/utils/formatters";
import { extractRupeeValue } from "lib/utils/coupons";
import { appendParamsToItemsUrl } from "lib/utils/checkout";
import { deleteRequest } from "lib/core/apiClient";
import { errorToast } from "lib/utils/toasters";

const InvalidOffersDialog = ({
  isOpen,
  handleClose,
  unavailabilityReasons,
  mode,
}: {
  isOpen: boolean;
  handleClose: () => void;
  unavailabilityReasons: UnApplicablePaymentReasonType[];
  mode: PaymentMode;
}) => {
  const {
    actions: { handleUnapplicablePaymentMethod },
    state: { paymentMethods },
  } = usePaymentContext();
  const {
    state: { partnerOffers, billing, checkoutId },
    actions: { setPartnerOffers },
  } = useCheckoutContext();
  const {
    state: { merchant },
  } = useMerchantContext();
  const primaryColor = merchant?.colorPallet?.primaryColor ?? "#5C0F8B";
  const { t } = useLocale();
  const [isLoading, setIsLoading] = useState(false);

  const hasBlockedPaymentModes = unavailabilityReasons?.some((r) => r.type === "PAYMENT_MODES") ?? false;

  const allInvalidDiscounts = unavailabilityReasons?.filter((r) => r.type === "INVALID_DISCOUNTS") ?? [];
  const invalidCodes = allInvalidDiscounts.flatMap((r) => r.values ?? []);
  const codeLabelMap = allInvalidDiscounts.reduce<Record<string, string>>((acc, r) => {
    return { ...acc, ...(r.code_title_mapping ?? r.title_code_mapping ?? {}) };
  }, {});

  const allPartnerOfferKeys = new Set(
    (partnerOffers ?? []).flatMap((o) => [o.offer_code, o.offer_id].filter(Boolean) as string[]),
  );

  const partnerInvalidCodes = invalidCodes.filter((code) => allPartnerOfferKeys.has(code));
  const nonPartnerInvalidCodes = invalidCodes.filter((code) => !allPartnerOfferKeys.has(code));

  const hasBlockingPartnerOffer = partnerInvalidCodes.length > 0;
  const hasNonPartnerOffers = nonPartnerInvalidCodes.length > 0;

  const blockingPartnerOffers = (partnerOffers ?? []).filter(
    (o) => o.blocks_cod && (o.offer_state === "COLLECTED" || o.offer_state === "ALREADY_APPLIED"),
  );

  const totalPartnerValue = blockingPartnerOffers.reduce(
    (sum, o) => sum + (o.offer_value ?? extractRupeeValue(o.title || o.description)),
    0,
  );

  const onlineMode = paymentMethods?.find((m: PaymentMode) => m.name === "PL" && m.isAvailable);
  const onlineAmount = onlineMode?.amount ?? billing?.total_payable ?? 0;

  const handleRemoveOffers = () => {
    handleClose();
    handleUnapplicablePaymentMethod(mode?.name, unavailabilityReasons);
  };

  const handlePlaceCOD = async () => {
    const blockingOfferIds = blockingPartnerOffers.map((o) => o.offer_id);
    try {
      setIsLoading(true);
      if (blockingOfferIds.length) {
        const discountPath = appendParamsToItemsUrl(`/checkout/${checkoutId}/discount`);
        await deleteRequest(discountPath, { partner_offer_ids: blockingOfferIds }, "KRATOS_PRIVATE");
        setPartnerOffers(
          (partnerOffers ?? []).map((o) =>
            blockingOfferIds.includes(o.offer_id) ? { ...o, offer_state: "APPLICABLE" } : o,
          ),
        );
      }
      handleClose();
      await handleUnapplicablePaymentMethod(
        mode?.name,
        unavailabilityReasons?.length ? unavailabilityReasons : [{ type: "INVALID_DISCOUNTS", values: [""] }],
      );
    } catch (e) {
      errorToast(t("something_went_wrong"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <GenericDialog
      isOpen={isOpen}
      setIsOpen={handleClose}
      translateAxis="y"
      customClass="overflow-scroll md:!top-auto md:absolute rounded-t-2xl max-h-[81vh]"
      modalType="REMOVE_OFFERS"
      dialogOverlay={true}>
      <div className="flex flex-col p-5 antialiased">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-text-lg-semibold text-coal-dark">{t("pay_online_and_save_more")}</h2>
          <button className="rounded outline-none hover:[background:color-mix(in_srgb,transparent,#000000_4%)]" onClick={handleClose} type="button">
            <Icon IconComponent={X} size="xxs" iconProps={{ weight: "regular" }} className="cursor-pointer text-zinc-500" />
          </button>
        </div>

        {/* Partner offers info row */}
        {hasBlockingPartnerOffer && (
          <div className="mt-4 flex items-start gap-2">
            <div className="relative shrink-0" style={{ width: "56px", height: "36.923px" }}>
              {blockingPartnerOffers.slice(0, 3).map((offer, i) =>
                offer.logo_url ? (
                  <img
                    key={offer.offer_id}
                    src={offer.logo_url}
                    alt={offer.title}
                    className="absolute top-0 shrink-0 rounded-full object-cover"
                    style={{
                      width: "36.923px",
                      height: "36.923px",
                      border: "1.538px solid rgba(9, 9, 11, 0.06)",
                      left: `${i * 8.923}px`,
                      zIndex: i,
                    }}
                  />
                ) : null,
              )}
            </div>
            <p className="text-text-sm-medium text-coal-light">
              {t("partner_offers.miss_partner_offers_prefix")}{" "}
              <span className="text-text-sm-semibold text-coal-dark">
                {totalPartnerValue > 0 ? currencyFormatter(totalPartnerValue) : t("partner_offers.these_vouchers")}
              </span>
              {t("partner_offers.miss_partner_offers_suffix")}
            </p>
          </div>
        )}

        {/* Non-partner invalid coupons */}
        {hasNonPartnerOffers && (
          <div className="mt-4 flex items-start gap-2">
            <div className="flex shrink-0 items-center justify-center" style={{ width: "56px" }}>
              <div className="relative shrink-0">
                <div
                  className="flex shrink-0 flex-col items-center justify-center rounded-full bg-[#ECFDF5]"
                  style={{ width: "36.923px", height: "36.923px", padding: "6.462px", border: "1.538px solid rgba(9, 9, 11, 0.06)" }}>
                  <Icon IconComponent={SealPercent} size="md" iconProps={{ weight: "fill" }} className="text-yay-dark" />
                </div>
                <div className="absolute flex items-center gap-2 rounded-full bg-ouch p-0.5" style={{ right: "-2px", top: "-3.462px" }}>
                  <Icon IconComponent={X} size="xxs" iconProps={{ weight: "bold" }} className="!h-3.5 !w-3.5 text-white" />
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <p className="text-text-sm-medium text-coal-light">{t("payment_method_unavailable_for_offers")}</p>
              <div className="flex flex-wrap gap-2">
                {nonPartnerInvalidCodes.map((code, index) => (
                  <Tag
                    key={index}
                    className="!flex !gap-2 !rounded-md !border !border-black/[0.12] !bg-transparent !px-1.5 !py-0.5 !text-text-xs-semibold !text-[#71717B]">
                    {codeLabelMap[code] ?? code}
                  </Tag>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Payment mode blocked by rewards */}
        {hasBlockedPaymentModes && (
          <div className="mt-4 flex items-start gap-2">
            <div className="flex shrink-0 items-center justify-center" style={{ width: "56px" }}>
              <div className="relative shrink-0">
                <div
                  className="flex shrink-0 flex-col items-center justify-center rounded-full bg-[#ECFDF5]"
                  style={{ width: "36.923px", height: "36.923px", padding: "6.462px", border: "1.538px solid rgba(9, 9, 11, 0.06)" }}>
                  <Icon IconComponent={SealPercent} size="md" iconProps={{ weight: "fill" }} className="text-yay-dark" />
                </div>
                <div className="absolute flex items-center gap-2 rounded-full bg-ouch p-0.5" style={{ right: "-2px", top: "-3.462px" }}>
                  <Icon IconComponent={X} size="xxs" iconProps={{ weight: "bold" }} className="!h-3.5 !w-3.5 text-white" />
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <p className="text-text-sm-medium text-coal-light">{t("payment_method_unavailable_for_offers")}</p>
              <div className="flex flex-wrap gap-2">
                <Tag className="!flex !gap-2 !rounded-md !border !border-black/[0.12] !bg-transparent !px-1.5 !py-0.5 !text-text-xs-semibold !text-[#71717B]">
                  {t("rewards")}
                </Tag>
              </div>
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className="mt-[22px] flex w-full flex-col gap-2">
          <button
            type="button"
            className="flex h-[52px] w-full items-center justify-center gap-2 rounded-xl px-5 text-white hover:[background:color-mix(in_srgb,var(--btn-bg),#000000_4%)]"
            style={{ backgroundColor: primaryColor, ["--btn-bg" as string]: primaryColor }}
            onClick={handleClose}>
            <span className="text-text-md-semibold">
              {hasBlockingPartnerOffer ? t("partner_offers.pay_online") : t("pay_with_offers")}
            </span>
            {hasBlockingPartnerOffer && onlineAmount > 0 && (
              <>
                <span className="text-text-md-semibold leading-none">·</span>
                <span className="text-text-md-semibold">{currencyFormatter(onlineAmount)}</span>
              </>
            )}
          </button>
          <button
            type="button"
            disabled={isLoading}
            onClick={hasBlockingPartnerOffer ? handlePlaceCOD : handleRemoveOffers}
            className="flex h-[52px] w-full items-center justify-center gap-2 rounded-xl border border-coal-dark/20 bg-white px-5 text-coal-dark hover:[background:color-mix(in_srgb,white,#000000_4%)] disabled:opacity-60">
            {isLoading ? (
              <CircularSpinner />
            ) : (
              <>
                <span className="text-text-md-semibold">
                  {hasBlockingPartnerOffer
                    ? mode?.name === "SPLIT_COD"
                      ? t("partner_offers.place_split_cod")
                      : t("partner_offers.place_cod")
                    : t("remove_offers")}
                </span>
              </>
            )}
          </button>
        </div>
      </div>
    </GenericDialog>
  );
};

export default InvalidOffersDialog;
